import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import type { DocumentoAnexado } from '../../tipos/tiposCriacaoPeticao';
import { formatarTamanhoArquivo } from '../../tipos/tiposDocumentos';
import {
    iniciarUploadAssincrono,
    verificarStatusUpload,
    obterResultadoUpload,
} from '../../servicos/servicoApiDocumentos';

const EXTENSOES_PERMITIDAS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.zip'];
const TAMANHO_MAXIMO_BYTES = 50 * 1024 * 1024; // 50MB

interface PropsCampoComAnexo {
    /** ID único do campo */
    campoId: string;

    /** ID do processo/petição para associar o documento */
    peticaoId: string;

    /** Nome/descrição do campo (usado como metadata no upload) */
    nomeCampo: string;

    /** Elemento input ou textarea a ser envolto */
    children: React.ReactNode;

    /** Documento atualmente anexado (se houver) */
    documentoAnexado?: DocumentoAnexado | null;

    /** Callback quando documento é anexado com sucesso */
    onDocumentoAnexado: (documento: DocumentoAnexado) => void;

    /** Callback quando documento é removido */
    onDocumentoRemovido: () => void;

    /** Callback de erro */
    onErro?: (mensagem: string) => void;
}

type StatusUpload = 'idle' | 'uploading' | 'completed' | 'error';

export function CampoComAnexo({
    // campoId, // unused
    peticaoId,
    nomeCampo,
    children,
    documentoAnexado,
    onDocumentoAnexado,
    onDocumentoRemovido,
    onErro,
}: PropsCampoComAnexo): JSX.Element {
    const [statusUpload, setStatusUpload] = useState<StatusUpload>('idle');
    const [progresso, setProgresso] = useState(0);
    const [erroUpload, setErroUpload] = useState<string>('');

    const validarArquivo = useCallback((arquivo: File): string | null => {
        const extensao = arquivo.name.substring(arquivo.name.lastIndexOf('.')).toLowerCase();

        if (!EXTENSOES_PERMITIDAS.includes(extensao)) {
            return `Tipo de arquivo não suportado. Aceitos: ${EXTENSOES_PERMITIDAS.join(', ')}`;
        }

        if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
            return `Arquivo muito grande (máx ${TAMANHO_MAXIMO_BYTES / (1024 * 1024)}MB)`;
        }

        return null;
    }, []);

    const processarUpload = useCallback(async (arquivo: File) => {
        setStatusUpload('uploading');
        setProgresso(0);
        setErroUpload('');

        try {
            // Iniciar upload assíncrono
            const inicio = await iniciarUploadAssincrono(
                arquivo,
                'outro', // categoria padrão
                peticaoId,
                nomeCampo // descrição = nome do campo
            );
            const uploadId = inicio.upload_id;

            // Polling de progresso
            let concluido = false;
            while (!concluido) {
                await new Promise((resolve) => setTimeout(resolve, 1500));
                const status = await verificarStatusUpload(uploadId);

                const progressoAtual = status.progresso_percentual ?? 0;
                setProgresso(progressoAtual);

                if (status.status === 'CONCLUIDO') {
                    concluido = true;

                    // Obter resultado final
                    const resultado = await obterResultadoUpload(uploadId);

                    const docAnexado: DocumentoAnexado = {
                        id: resultado.documento_id,
                        nome: resultado.nome_arquivo || arquivo.name,
                        tipo: resultado.tipo_documento || arquivo.type,
                        tamanho: resultado.tamanho_bytes || arquivo.size,
                        dataUpload: new Date().toISOString(),
                        categoria: 'outro',
                        descricao: nomeCampo,
                    };

                    setStatusUpload('completed');
                    onDocumentoAnexado(docAnexado);

                } else if (status.status === 'ERRO') {
                    concluido = true;
                    const mensagemErro = status.mensagem_erro || 'Erro ao processar arquivo';
                    setStatusUpload('error');
                    setErroUpload(mensagemErro);
                    if (onErro) onErro(mensagemErro);
                }
            }
        } catch (err) {
            const mensagem = err instanceof Error ? err.message : 'Erro no upload';
            setStatusUpload('error');
            setErroUpload(mensagem);
            if (onErro) onErro(mensagem);
        }
    }, [peticaoId, nomeCampo, onDocumentoAnexado, onErro]);

    const onDrop = useCallback((arquivosAceitos: File[]) => {
        if (arquivosAceitos.length === 0) return;

        const arquivo = arquivosAceitos[0]; // apenas 1 arquivo por campo
        const erro = validarArquivo(arquivo);

        if (erro) {
            setStatusUpload('error');
            setErroUpload(erro);
            if (onErro) onErro(erro);
            return;
        }

        void processarUpload(arquivo);
    }, [validarArquivo, processarUpload, onErro]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false, // apenas 1 arquivo por campo
        maxSize: TAMANHO_MAXIMO_BYTES,
        disabled: statusUpload === 'uploading' || !!documentoAnexado,
    });

    const handleRemover = useCallback(() => {
        setStatusUpload('idle');
        setProgresso(0);
        setErroUpload('');
        onDocumentoRemovido();
    }, [onDocumentoRemovido]);

    const mostrarDropzone = !documentoAnexado && statusUpload !== 'uploading';

    return (
        <div className="w-full space-y-2">
            {/* Input/Textarea original */}
            <div className="w-full">{children}</div>

            {/* Dropzone compacto para anexar arquivo */}
            {mostrarDropzone && (
                <div
                    {...getRootProps()}
                    className={`w-full cursor-pointer rounded border border-dashed px-3 py-2 transition-colors ${isDragActive
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-300 bg-gray-50 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                >
                    <input {...getInputProps()} />
                    <div className="flex items-center gap-2">
                        <Upload className="size-3.5 text-gray-400" />
                        <span className="text-xs text-gray-600">
                            {isDragActive ? 'Solte o arquivo aqui' : 'Anexar arquivo (PDF, DOC, DOCX, JPG, PNG, ZIP · máx 50MB)'}
                        </span>
                    </div>
                </div>
            )}

            {/* Progresso de upload compacto */}
            {statusUpload === 'uploading' && (
                <div className="w-full rounded border border-blue-200 bg-blue-50 px-3 py-2">
                    <div className="flex items-center gap-2 text-xs text-blue-900">
                        <Loader2 className="size-3.5 animate-spin" />
                        <span>Enviando... {progresso}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full rounded-full bg-blue-200">
                        <div
                            style={{ width: `${progresso}%` }}
                            className="h-1.5 rounded-full bg-blue-600 transition-all"
                        />
                    </div>
                </div>
            )}

            {/* Documento anexado compacto */}
            {documentoAnexado && statusUpload === 'completed' && (
                <div className="flex w-full items-center gap-2 rounded border border-green-200 bg-green-50 px-3 py-2">
                    <CheckCircle className="size-3.5 shrink-0 text-green-600" />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                            <File className="size-3.5 shrink-0 text-green-600" />
                            <p className="truncate text-xs font-medium text-green-900">{documentoAnexado.nome}</p>
                        </div>
                        <p className="text-xs text-green-700">
                            {formatarTamanhoArquivo(documentoAnexado.tamanho)}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleRemover}
                        className="shrink-0 rounded p-0.5 text-green-600 hover:bg-green-100 hover:text-green-800"
                        title="Remover arquivo"
                    >
                        <X className="size-3.5" />
                    </button>
                </div>
            )}

            {/* Erro de upload */}
            {statusUpload === 'error' && (
                <div className="flex w-full items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                    <AlertCircle className="size-3.5 shrink-0" />
                    <span>{erroUpload}</span>
                </div>
            )}  </div>
    );
}

export default CampoComAnexo;
